import { fetcher } from '@commenda/utils';
import type {
  ICreateTransferPricingDocumentationResponse,
  ICreateTransferPricingDocumentationVersionResponse,
  IStartTransferPricingDocumentationWorkflowResponse,
  ITransferPricingDocumentation,
  ITransferPricingDocumentationListParams,
  ITransferPricingDocumentationStatusCount,
  TTransferPricingDocumentationMutationResult,
} from './types';

type TDocumentationRequestMethod = 'POST' | 'PATCH' | 'DELETE';

interface ISendDocumentationRequestParams<TData> {
  path: string;
  method: TDocumentationRequestMethod;
  body?: Record<string, unknown>;
  isExpectedBody: (body: unknown) => body is TData;
  fallbackErrorMessage: string;
  token?: string;
}

/** Paths, body guards and request plumbing for the documentation endpoints: one place that reads
 * the server message, checks `response.ok`, and validates the body before a caller trusts it. */
export class TransferPricingDocumentationRequestHelper {
  static readonly BASE_PATH = 'transfer-pricing/documentation';
  static readonly LOCAL_FILE_SERVICE_ID = 'TP_DOC_LOCALFILE';

  static readonly NO_DOCUMENTATION: ITransferPricingDocumentation[] = [];

  static readonly NO_STATUS_COUNTS: ITransferPricingDocumentationStatusCount[] =
    [];

  static recordPath(documentationId: string): string {
    return `${this.BASE_PATH}/${documentationId}`;
  }

  static detailsPath(documentationId: string): string {
    return `${this.recordPath(documentationId)}/details`;
  }

  static listPath(params: ITransferPricingDocumentationListParams): string {
    const query = new URLSearchParams({ companyId: String(params.companyId) });
    if (params.kind !== undefined) query.set('kind', params.kind);
    if (params.fiscalYear !== undefined) {
      query.set('fiscalYear', String(params.fiscalYear));
    }
    if (params.status !== undefined) query.set('status', params.status);
    return `${this.BASE_PATH}?${query.toString()}`;
  }

  static kindsPath(companyId: number): string {
    return `${this.BASE_PATH}/kinds?companyId=${companyId}`;
  }

  static readServerMessage(body: unknown): string | undefined {
    if (typeof body !== 'object' || body === null || !('message' in body)) {
      return undefined;
    }
    const { message } = body;
    if (typeof message === 'string') return message;
    if (Array.isArray(message) && typeof message[0] === 'string') {
      return message[0];
    }
    return undefined;
  }

  // The guards below are passed as detached callbacks, so they name the class instead of `this`.
  static hasDocumentation(
    body: unknown
  ): body is { documentation: ITransferPricingDocumentation } {
    return (
      typeof body === 'object' &&
      body !== null &&
      'documentation' in body &&
      typeof body.documentation === 'object' &&
      body.documentation !== null
    );
  }

  static isCreateVersionResponse(
    body: unknown
  ): body is ICreateTransferPricingDocumentationVersionResponse {
    return (
      TransferPricingDocumentationRequestHelper.hasDocumentation(body) &&
      'version' in body &&
      typeof body.version === 'object' &&
      body.version !== null
    );
  }

  static isCreateResponse(
    body: unknown
  ): body is ICreateTransferPricingDocumentationResponse {
    return (
      TransferPricingDocumentationRequestHelper.hasDocumentation(body) &&
      (!('serviceRequestId' in body) ||
        typeof body.serviceRequestId === 'string')
    );
  }

  static isStartWorkflowResponse(
    body: unknown
  ): body is IStartTransferPricingDocumentationWorkflowResponse {
    return (
      TransferPricingDocumentationRequestHelper.hasDocumentation(body) &&
      'serviceRequestId' in body &&
      typeof body.serviceRequestId === 'string'
    );
  }

  static async send<TData>({
    path,
    method,
    body,
    isExpectedBody,
    fallbackErrorMessage,
    token,
  }: ISendDocumentationRequestParams<TData>): Promise<
    TTransferPricingDocumentationMutationResult<TData>
  > {
    try {
      const response = await fetcher(
        path,
        body === undefined ? { method } : { method, body },
        token
      );
      const responseBody: unknown = await response.json().catch(() => null);

      if (!response.ok) {
        return {
          success: false,
          error: this.readServerMessage(responseBody) ?? fallbackErrorMessage,
          status: response.status,
        };
      }
      if (!isExpectedBody(responseBody)) {
        return { success: false, error: fallbackErrorMessage, status: null };
      }
      return { success: true, data: responseBody };
    } catch (error) {
      console.error(error);
      return { success: false, error: fallbackErrorMessage, status: null };
    }
  }
}
